/**
 * LD Cart UI - User interface operations for cart drawer
 */

class LDCartUI {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
  }

  show() {
    if (this.cartDrawer.isOpen) return;
    
    console.log('LD Cart UI: Opening drawer...');
    
    // Show the drawer
    this.cartDrawer.drawer.classList.add('drawer--open');
    this.cartDrawer.isOpen = true;
    
    // Focus management
    this.cartDrawer.drawer.focus();
    
    // Dispatch event
    document.dispatchEvent(new CustomEvent('ld:cart-drawer:opened', {
      bubbles: true,
      detail: { source: 'ld-cart-drawer' }
    }));
    
    console.log('LD Cart UI: Drawer opened');
  }

  hide() {
    if (!this.cartDrawer.isOpen) return;
    
    console.log('LD Cart UI: Closing drawer...');
    
    // Hide the drawer
    this.cartDrawer.drawer.classList.remove('drawer--open');
    this.cartDrawer.isOpen = false;
    
    // Return focus to trigger element if possible
    const trigger = document.querySelector('[data-ld-cart-trigger]:focus');
    if (trigger) {
      trigger.focus();
    }
    
    // Dispatch event
    document.dispatchEvent(new CustomEvent('ld:cart-drawer:closed', {
      bubbles: true,
      detail: { source: 'ld-cart-drawer' }
    }));
    
    console.log('LD Cart UI: Drawer closed');
  }

  toggle() {
    if (this.cartDrawer.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  updateCartCount(itemCount) {
    // Update cart count indicators throughout the site
    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble');
    
    cartCountElements.forEach(element => {
      if (element.tagName === 'INPUT') {
        element.value = itemCount;
      } else {
        element.textContent = itemCount;
      }
      
      // Show/hide based on count
      if (itemCount > 0) {
        element.classList.remove('hidden');
        element.style.display = '';
      } else {
        element.classList.add('hidden');
        element.style.display = 'none';
      }
    });
    
    // Dispatch cart count update event
    document.dispatchEvent(new CustomEvent('ld:cart:count:updated', {
      bubbles: true,
      detail: { itemCount }
    }));
  }

  setupEventListeners() {
    // Remove existing event listeners to prevent duplicates
    this.removeEventListeners();
    
    // Close button
    const closeButton = this.cartDrawer.drawer.querySelector('.ld-cart-drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    // Cart trigger buttons - only add once globally
    if (!this.cartDrawer.cartTriggerListenerAdded) {
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-ld-cart-trigger]')) {
          console.log('LD Cart UI: Cart trigger clicked:', e.target.closest('[data-ld-cart-trigger]'));
          e.preventDefault();
          this.show();
        }
      });
      this.cartDrawer.cartTriggerListenerAdded = true;
    }
 
    // Quantity selectors and other cart item interactions
    const cartItemsContainer = this.cartDrawer.drawer.querySelector('.ld-cart-drawer__items');
    if (cartItemsContainer) {
      cartItemsContainer.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="decrease"]')) {
          const lineItemId = e.target.closest('[data-line-item-id]').dataset.lineItemId;
          console.log('LD Cart UI: Decrease button clicked for line item:', lineItemId);
          this.cartDrawer.operations.updateQuantity(lineItemId, -1);
        } else if (e.target.closest('[data-action="increase"]')) {
          const lineItemId = e.target.closest('[data-line-item-id]').dataset.lineItemId;
          console.log('LD Cart UI: Increase button clicked for line item:', lineItemId);
          this.cartDrawer.operations.updateQuantity(lineItemId, 1);
        } else if (e.target.closest('[data-action="remove-line-item"]')) {
          const lineItemId = e.target.closest('[data-action="remove-line-item"]').dataset.lineItemId;
          console.log('LD Cart UI: Remove button clicked for line item:', lineItemId);
          this.cartDrawer.operations.removeLineItem(lineItemId);
        }
      });

      // Quantity input changes
      cartItemsContainer.addEventListener('change', (e) => {
        if (e.target.matches('.quantity-selector__input')) {
          const lineItemId = e.target.dataset.lineItemId;
          const newQuantity = parseInt(e.target.value);
          if (newQuantity >= 0) {
            this.cartDrawer.operations.setQuantity(lineItemId, newQuantity);
          }
        }
      });
    }

    // Cross-sell add to cart buttons
    const crossSellSection = this.cartDrawer.drawer.querySelector('.ld-cart-drawer__additional-products');
    if (crossSellSection) {
      crossSellSection.addEventListener('click', (e) => {
        const addButton = e.target.closest('.ld-cart-drawer__add-to-cart');
        if (addButton) {
          const variantId = addButton.dataset.variantId;
          const productId = addButton.dataset.productId;
          console.log('LD Cart UI: Cross-sell add to cart clicked', { productId, variantId });
          if (variantId) {
            this.cartDrawer.addToCartAndShow(variantId, 1);
          } else if (this.cartDrawer.crossSell) {
            this.cartDrawer.crossSell.addCrossSellProduct(productId);
          }
        }
      });
    }

    // Note section functionality
    const noteToggle = this.cartDrawer.drawer.querySelector('.ld-cart-drawer__note-toggle');
    const noteInput = this.cartDrawer.drawer.querySelector('.ld-cart-drawer__note-input');
    
    if (noteToggle && noteInput) {
      noteToggle.addEventListener('click', function() {
        if (noteInput.style.display === 'none') {
          noteInput.style.display = 'block';
        } else {
          noteInput.style.display = 'none';
        }
      });
    }

    // Click outside to close - only add once globally
    if (!this.cartDrawer.clickOutsideListenerAdded) {
      document.addEventListener('click', (e) => {
        if (this.cartDrawer.isOpen && !this.cartDrawer.drawer.contains(e.target) && !e.target.closest('[data-ld-cart-trigger]')) {
          this.hide();
        }
      });
      this.cartDrawer.clickOutsideListenerAdded = true;
    }

    // Escape key to close - only add once globally
    if (!this.cartDrawer.escapeKeyListenerAdded) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.cartDrawer.isOpen) {
          this.hide();
        }
      });
      this.cartDrawer.escapeKeyListenerAdded = true;
    }
  }

  removeEventListeners() {
    // Event listeners are automatically cleaned up when elements are replaced
    // No need to manually remove them as setupEventListeners will handle new ones
  }
}

// Make class globally available
window.LDCartUI = LDCartUI;
